// A thin fetch wrapper over the Go /admin/* surface. It is auth-agnostic:
// callers pass a `getToken` function (from Clerk's useAuth) so the client can
// attach a bearer token to every request. See useApi() for the wired hook.

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8080";

// Every staff endpoint lives under /admin. The merchant surfaces (/api/v1,
// /api/v2) authenticate with API keys and are not reachable with a Clerk token,
// so this client only ever speaks to one prefix.
const ADMIN_BASE = `${API_URL.replace(/\/+$/, "")}/admin`;

export type GetToken = (opts?: { template?: string }) => Promise<string | null>;

/**
 * The envelope every altura endpoint returns, success or failure.
 * See internal/api/response/response.go.
 */
interface Envelope<T> {
  status: number;
  code?: string;
  message: string;
  data?: T;
}

/** Machine-readable error codes from the Go admin surface. */
export const ErrorCode = {
  Unauthenticated: "unauthenticated",
  NotStaff: "not_staff",
  NotFound: "not_found",
  ValidationFailed: "validation_failed",
  Conflict: "conflict",
  UpstreamError: "upstream_error",
  RateLimited: "rate_limited",
  /** 422 on a fee schedule priced under Kugie's cost with no override. */
  BelowCost: "below_cost",
  /** 422 on a statement over a period with nothing left to put on one. */
  NothingToInvoice: "nothing_to_invoice",
  /** 422 on a payout: the balance is below the statement's net payable. */
  InsufficientBalance: "insufficient_balance",
  /** 422 on a payout: the statement's net payable is zero or negative. */
  NothingToPayOut: "nothing_to_pay_out",
  /** 422 on a payout: merchants.status is suspended. */
  MerchantSuspended: "merchant_suspended",
  /** 422 on a payout: the merchant has no complete bank details on file. */
  NoBankAccount: "no_bank_account",
  InternalError: "internal_error",
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];

/**
 * The shape of every admin list endpoint's payload.
 * See internal/api/handler/admin/pagination.go.
 */
export interface Pagination {
  limit: number;
  offset: number;
  total: number;
  /** True when `total` is approximate. Never render it as exact. */
  estimated: boolean;
}

export interface ListPayload<T> {
  items: T[];
  pagination: Pagination;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/*
 * Branch predicates. Callers use these rather than comparing `message`, which
 * is prose written for a human and will be reworded without warning. The four
 * cases below are the four the SPA is required to handle distinctly.
 */

/** 401 — no session, or one that did not verify. Re-authenticate. */
export function isUnauthenticated(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}

/** 403 not_staff — a real person with no staff row. Show the access screen. */
export function isNotStaff(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403 && e.code === ErrorCode.NotStaff;
}

/** 404 — the record does not exist. Show an empty state, not a crash. */
export function isNotFound(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404;
}

/**
 * 409 — the write collided with a record that already exists.
 *
 * Always answered next to the field that caused it, never as a toast: the value
 * that collided is on screen, and so is the person who typed it.
 */
export function isConflict(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409 && e.code === ErrorCode.Conflict;
}

/**
 * 422 below_cost — a fee schedule under Kugie's cost with no override. Not an
 * error to show and forget: the form answers it by asking for the override
 * and a reason, then resubmits.
 */
export function isBelowCost(e: unknown): boolean {
  return e instanceof ApiError && e.status === 422 && e.code === ErrorCode.BelowCost;
}

/**
 * An error carrying one specific code, for the refusals that each need their
 * own sentence (a payout with no bank account is a different next step from
 * one the balance cannot cover).
 */
export function hasCode(e: unknown, code: ErrorCodeValue): boolean {
  return e instanceof ApiError && e.code === code;
}

/**
 * 429 — asking too often.
 *
 * Only the provider lookup answers this. It is the one admin route whose cost
 * lands on a partner, and it is behind a button someone will press repeatedly
 * at exactly the moment things are already going wrong. Worth its own message:
 * "wait a moment" is a different instruction from "it broke".
 */
export function isRateLimited(e: unknown): boolean {
  return e instanceof ApiError && e.status === 429;
}

/**
 * 502 — a dependency answered badly or not at all.
 *
 * Distinct from isServerError: our server is fine and something downstream is
 * not, which is a different sentence and a different next step.
 */
export function isUpstreamError(e: unknown): boolean {
  return e instanceof ApiError && e.code === ErrorCode.UpstreamError;
}

/** 5xx or a transport failure. Toast it and keep what is already on screen. */
export function isServerError(e: unknown): boolean {
  if (e instanceof ApiError) return e.status >= 500;
  // A network failure never became a response at all, and is the same class of
  // problem from the user's point of view: the server did not answer.
  return e instanceof Error && e.name === "TypeError";
}

// A 502 from a proxy is an HTML page, not an envelope. Parsing defensively
// keeps that path on the 5xx branch instead of throwing a SyntaxError that no
// caller is looking for.
function parseEnvelope<T>(text: string): Envelope<T> | null {
  try {
    return text ? (JSON.parse(text) as Envelope<T>) : null;
  } catch {
    return null;
  }
}

function toApiError(res: Response, text: string): ApiError {
  const envelope = parseEnvelope<unknown>(text);
  return new ApiError(res.status, envelope?.message || res.statusText, envelope?.code);
}

export class ApiClient {
  private readonly getToken: GetToken;

  constructor(getToken: GetToken) {
    this.getToken = getToken;
  }

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    // No template argument. The plain Clerk session token is exactly what the
    // Go middleware verifies against Clerk's JWKS (internal/auth/
    // verifier_clerk.go). A custom JWT template would be a second thing to keep
    // in sync across Clerk and the server for no benefit.
    const token = await this.getToken();

    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";

    const res = await fetch(`${ADMIN_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    if (!res.ok) throw toApiError(res, text);

    // The payload lives under `data`; the envelope's other fields are transport
    // concerns. Endpoints that return nothing have no `data` at all.
    return parseEnvelope<T>(text)?.data as T;
  }

  /**
   * GET a route that answers with a body rather than an envelope -- today only
   * the statement document, which is server-rendered HTML. Errors still come
   * back as envelopes and throw the same ApiError.
   */
  async getText(path: string): Promise<string> {
    const token = await this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${ADMIN_BASE}${path}`, { headers });
    const text = await res.text();
    if (!res.ok) throw toApiError(res, text);
    return text;
  }

  get<T>(path: string) {
    return this.request<T>("GET", path);
  }
  post<T>(path: string, body?: unknown) {
    return this.request<T>("POST", path, body);
  }
  put<T>(path: string, body?: unknown) {
    return this.request<T>("PUT", path, body);
  }
  patch<T>(path: string, body?: unknown) {
    return this.request<T>("PATCH", path, body);
  }
  del<T>(path: string, body?: unknown) {
    return this.request<T>("DELETE", path, body);
  }
}

/** Extracts something worth showing a human from an unknown thrown value. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong";
}

export { ADMIN_BASE, API_URL };
